import React, {
    useEffect, useState, useCallback, useMemo,
} from 'react';
import PropTypes, { InferProps } from 'prop-types';

import classnames from 'classnames/bind';

import { useUpdateDocumentSettingsMutation, DocumentSettings } from '@SERVICES';
import {
    generateDetailedDocumentColors,
    DocumentColors,
    DetailedDocumentColors,
} from '@HELPERS/colorUtils';

import { notify } from '@NOTIFICATION/Notificator';

import BasicInput from '@COMPONENTS/COMMON/inputs/input/BasicInput';
import BasicTextarea from '@COMPONENTS/COMMON/inputs/textarea/BasicTextarea';
import BasicButton from '@COMPONENTS/COMMON/buttons/BasicButton';
import FileUploadDropzone from '@COMPONENTS/COMMON/inputs/file/FileUploadDropzone';

import styles from './DocumentSettingsTab.module.scss';

const cx: CX = classnames.bind(styles);

function DocumentSettingsTab(props: Props) {
    const { documentSettingsData } = props;

    const [updateDocumentSettings, { isLoading: isUpdating }] = useUpdateDocumentSettingsMutation();

    const [documentHeaderImageFile, setDocumentHeaderImageFile] = useState<File | null>(null);

    // Form state for document settings
    const [formData, setFormData] = useState({
        header_text: documentSettingsData.header_text || '',
        footer_text: documentSettingsData.footer_text || '',
        confidentiality_note_text: documentSettingsData.confidentiality_note_text || '',
        // Main document colors
        document_primary_color: documentSettingsData.document_primary_color || '#00a4ba',
        document_secondary_color: documentSettingsData.document_secondary_color || '#00a4ba',
        document_text_color: documentSettingsData.document_text_color || '#000000',
        // Detailed colors
        header_text_color: documentSettingsData.header_text_color || '#000000',
        footer_text_color: documentSettingsData.footer_text_color || '#000000',
        confidentiality_note_color: documentSettingsData.confidentiality_note_color || '#000000',
        document_param_text_color: documentSettingsData.document_param_text_color || '#000000',
        document_release_text_color: documentSettingsData.document_release_text_color || '#000000',
        general_title_color: documentSettingsData.general_title_color || '#000000',
        toc_chapter_title_color: documentSettingsData.toc_chapter_title_color || '#000000',
        toc_section_title_color: documentSettingsData.toc_section_title_color || '#000000',
        chapter_cover_page_color: documentSettingsData.chapter_cover_page_color || '#000000',
        vendor_email_color: documentSettingsData.vendor_email_color || '#2E90FA',
        vendor_section_color: documentSettingsData.vendor_section_color || '#00a4ba',
    });

    const [showDetailedColors, setShowDetailedColors] = useState(false);
    const [manualOverrides, setManualOverrides] = useState<Set<string>>(new Set());

    // Generate detailed colors from main document colors
    const detailedColors = useMemo(() => {
        const documentColors: DocumentColors = {
            documentPrimaryColor: formData.document_primary_color,
            documentSecondaryColor: formData.document_secondary_color,
            documentTextColor: formData.document_text_color,
        };
        return generateDetailedDocumentColors(documentColors);
    }, [formData.document_primary_color, formData.document_secondary_color, formData.document_text_color]);

    // Update form state when documentSettingsData changes
    useEffect(() => {
        setFormData({
            header_text: documentSettingsData.header_text || '',
            footer_text: documentSettingsData.footer_text || '',
            confidentiality_note_text: documentSettingsData.confidentiality_note_text || '',
            document_primary_color: documentSettingsData.document_primary_color || '#00a4ba',
            document_secondary_color: documentSettingsData.document_secondary_color || '#00a4ba',
            document_text_color: documentSettingsData.document_text_color || '#000000',
            header_text_color: documentSettingsData.header_text_color || '#000000',
            footer_text_color: documentSettingsData.footer_text_color || '#000000',
            confidentiality_note_color: documentSettingsData.confidentiality_note_color || '#000000',
            document_param_text_color: documentSettingsData.document_param_text_color || '#000000',
            document_release_text_color: documentSettingsData.document_release_text_color || '#000000',
            general_title_color: documentSettingsData.general_title_color || '#000000',
            toc_chapter_title_color: documentSettingsData.toc_chapter_title_color || '#000000',
            toc_section_title_color: documentSettingsData.toc_section_title_color || '#000000',
            chapter_cover_page_color: documentSettingsData.chapter_cover_page_color || '#000000',
            vendor_email_color: documentSettingsData.vendor_email_color || '#2E90FA',
            vendor_section_color: documentSettingsData.vendor_section_color || '#00a4ba',
        });
    }, [documentSettingsData]);

    // Auto-generate detailed colors when main colors change, unless manually overridden
    useEffect(() => {
        Object.entries(detailedColors).forEach(([key, value]) => {
            if (!manualOverrides.has(key)) {
                setFormData((prev) => ({
                    ...prev,
                    [key]: value,
                }));
            }
        });
    }, [detailedColors, manualOverrides]);

    const handleInputChange = (field: keyof typeof formData, value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    };

    const handleMainColorChange = useCallback((field: 'document_primary_color' | 'document_secondary_color' | 'document_text_color', value: string) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleDetailedColorChange = useCallback((field: keyof DetailedDocumentColors, value: string) => {
        // Mark this color as manually overridden
        setManualOverrides((prev) => new Set(prev).add(field));
        // Update the form data
        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleDocumentHeaderImageUpload = (files: File[]) => {
        if (files.length > 0) {
            setDocumentHeaderImageFile(files[0]);
        }
    };

    const handleDocumentHeaderImageClear = () => {
        setDocumentHeaderImageFile(null);
    };

    const handleSubmit = async () => {
        try {
            const updateData: any = { ...formData };

            // Add document header image file if uploaded
            if (documentHeaderImageFile) {
                updateData.document_header_image = documentHeaderImageFile;
            }

            await updateDocumentSettings(updateData).unwrap();

            notify.success('Document settings updated successfully!', {
                toastId: 'document-settings-success',
            });
        } catch (error) {
            notify.error('Failed to update document settings!', {
                toastId: 'document-settings-error',
            });
        }
    };

    return (
        <div className={cx('document-settings-tab')}>
            <div className={cx('form-section')}>
                <h2>Document Content</h2>
                <p className={cx('section-description')}>
                    These settings control the text content that appears in generated PDF documents.
                </p>

                <div className={cx('form-group')}>
                    <BasicInput
                        value={formData.header_text}
                        onChange={(value) => handleInputChange('header_text', value)}
                        label="Header Text"
                        placeholder="Enter header text for documents"
                    />
                </div>

                <div className={cx('form-group')}>
                    <BasicInput
                        value={formData.footer_text}
                        onChange={(value) => handleInputChange('footer_text', value)}
                        label="Footer Text"
                        placeholder="Enter footer text for documents"
                    />
                </div>

                <div className={cx('form-group')}>
                    <BasicTextarea
                        value={formData.confidentiality_note_text}
                        onChange={(value) => handleInputChange('confidentiality_note_text', value)}
                        title="Confidentiality Note"
                        placeholder="Enter confidentiality note for documents"
                    />
                </div>
            </div>

            <div className={cx('form-section')}>
                <h2>Document Header Image</h2>
                <p className={cx('section-description')}>
                    Upload a header image that will appear in generated PDF documents. This is separate from the application logo.
                </p>

                <div className={cx('form-group')}>
                    <FileUploadDropzone
                        label="Document Header Image"
                        title="Click or drag header image to upload"
                        onDrop={handleDocumentHeaderImageUpload}
                        selectedFile={documentHeaderImageFile}
                        onClear={handleDocumentHeaderImageClear}
                        accept={{
                            'image/jpeg': [],
                            'image/png': [],
                        }}
                        helpText="JPG or PNG, max 5MB, recommended 1200x300px"
                        maxSize={5 * 1024 * 1024}
                    />
                    {!documentHeaderImageFile && documentSettingsData.document_header_image && (
                        <div className={cx('current-image')}>
                            <p>
                                Current header image:
                                {' '}
                                {documentSettingsData.document_header_image}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            <div className={cx('form-section')}>
                <div className={cx('document-colors-section')}>
                    <h3>Document Colors</h3>
                    <p className={cx('section-description')}>
                        Set your 3 main document colors below. All PDF document colors will be automatically calculated from these - you don&apos;t need to set anything else unless you want to override specific colors.
                    </p>

                    <div className={cx('main-colors-grid')}>
                        <div className={cx('color-group')}>
                            <label htmlFor="document_primary_color">
                                <span className={cx('color-name')}>Primary Color</span>
                                <span className={cx('color-usage')}>Used for document headers, titles, and primary elements</span>
                            </label>
                            <div className={cx('color-input-wrapper')}>
                                <input
                                    id="document_primary_color"
                                    type="color"
                                    value={formData.document_primary_color}
                                    onChange={(e) => handleMainColorChange('document_primary_color', e.target.value)}
                                    className={cx('color-input')}
                                />
                                <span className={cx('color-value')}>{formData.document_primary_color}</span>
                            </div>
                        </div>

                        <div className={cx('color-group')}>
                            <label htmlFor="document_secondary_color">
                                <span className={cx('color-name')}>Secondary Color</span>
                                <span className={cx('color-usage')}>Used for vendor sections and highlights in documents</span>
                            </label>
                            <div className={cx('color-input-wrapper')}>
                                <input
                                    id="document_secondary_color"
                                    type="color"
                                    value={formData.document_secondary_color}
                                    onChange={(e) => handleMainColorChange('document_secondary_color', e.target.value)}
                                    className={cx('color-input')}
                                />
                                <span className={cx('color-value')}>{formData.document_secondary_color}</span>
                            </div>
                        </div>

                        <div className={cx('color-group')}>
                            <label htmlFor="document_text_color">
                                <span className={cx('color-name')}>Text Color</span>
                                <span className={cx('color-usage')}>Used for document text elements and details</span>
                            </label>
                            <div className={cx('color-input-wrapper')}>
                                <input
                                    id="document_text_color"
                                    type="color"
                                    value={formData.document_text_color}
                                    onChange={(e) => handleMainColorChange('document_text_color', e.target.value)}
                                    className={cx('color-input')}
                                />
                                <span className={cx('color-value')}>{formData.document_text_color}</span>
                            </div>
                        </div>
                    </div>

                    <div className={cx('detailed-colors-toggle')}>
                        <button
                            type="button"
                            onClick={() => setShowDetailedColors(!showDetailedColors)}
                            className={cx('toggle-button')}
                        >
                            {showDetailedColors ? 'Hide' : 'Show'}
                            {' '}
                            Advanced PDF Document Colors
                        </button>
                        <p className={cx('toggle-description')}>
                            ⚠️ Advanced: These colors are automatically set from your main colors above. Only change these if you need to override the automatic calculation.
                        </p>
                    </div>

                    {showDetailedColors && (
                        <div className={cx('detailed-colors-grid')}>
                            <div className={cx('color-category')}>
                                <h4>Primary Color Elements</h4>
                                <div className={cx('category-colors')}>
                                    {[
                                        { key: 'header_text_color', label: 'Header Text Color' },
                                        { key: 'chapter_cover_page_color', label: 'Chapter Cover Page Color' },
                                        { key: 'toc_chapter_title_color', label: 'Table of Content Chapter Title Color' },
                                        { key: 'toc_section_title_color', label: 'Table of Content Section Title Color' },
                                        { key: 'general_title_color', label: 'General Title Color' },
                                        { key: 'document_param_text_color', label: 'Document Param Text Color' },
                                        { key: 'document_release_text_color', label: 'Document Release Text Color' },
                                    ].map(({ key, label }) => (
                                        <div key={key} className={cx('detailed-color-group')}>
                                            <label htmlFor={key}>{label}</label>
                                            <div className={cx('detailed-color-input-wrapper')}>
                                                <input
                                                    id={key}
                                                    type="color"
                                                    value={formData[key as keyof typeof formData] as string}
                                                    onChange={(e) => handleDetailedColorChange(key as keyof DetailedDocumentColors, e.target.value)}
                                                    className={cx('detailed-color-input')}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={cx('color-category')}>
                                <h4>Text Color Elements</h4>
                                <div className={cx('category-colors')}>
                                    {[
                                        { key: 'footer_text_color', label: 'Footer Text Color' },
                                        { key: 'confidentiality_note_color', label: 'Confidentiality Note Color' },
                                        { key: 'vendor_email_color', label: 'Vendor Email Color' },
                                    ].map(({ key, label }) => (
                                        <div key={key} className={cx('detailed-color-group')}>
                                            <label htmlFor={key}>{label}</label>
                                            <div className={cx('detailed-color-input-wrapper')}>
                                                <input
                                                    id={key}
                                                    type="color"
                                                    value={formData[key as keyof typeof formData] as string}
                                                    onChange={(e) => handleDetailedColorChange(key as keyof DetailedDocumentColors, e.target.value)}
                                                    className={cx('detailed-color-input')}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className={cx('color-category')}>
                                <h4>Secondary Color Elements</h4>
                                <div className={cx('category-colors')}>
                                    {[
                                        { key: 'vendor_section_color', label: 'Vendor Section Color' },
                                    ].map(({ key, label }) => (
                                        <div key={key} className={cx('detailed-color-group')}>
                                            <label htmlFor={key}>{label}</label>
                                            <div className={cx('detailed-color-input-wrapper')}>
                                                <input
                                                    id={key}
                                                    type="color"
                                                    value={formData[key as keyof typeof formData] as string}
                                                    onChange={(e) => handleDetailedColorChange(key as keyof DetailedDocumentColors, e.target.value)}
                                                    className={cx('detailed-color-input')}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className={cx('form-actions')}>
                    <BasicButton
                        onClick={handleSubmit}
                        isProcessing={isUpdating}
                        title="Save Document Settings"
                        variant="primary"
                        size="medium"
                    />
                </div>
            </div>
        </div>
    );
}

DocumentSettingsTab.propTypes = {
    documentSettingsData: PropTypes.shape({
        id: PropTypes.number,
        document_header_image: PropTypes.string,
        header_text: PropTypes.string,
        footer_text: PropTypes.string,
        confidentiality_note_text: PropTypes.string,
        document_primary_color: PropTypes.string.isRequired,
        document_secondary_color: PropTypes.string.isRequired,
        document_text_color: PropTypes.string.isRequired,
        header_text_color: PropTypes.string.isRequired,
        footer_text_color: PropTypes.string.isRequired,
        confidentiality_note_color: PropTypes.string.isRequired,
        document_param_text_color: PropTypes.string.isRequired,
        document_release_text_color: PropTypes.string.isRequired,
        general_title_color: PropTypes.string.isRequired,
        toc_chapter_title_color: PropTypes.string.isRequired,
        toc_section_title_color: PropTypes.string.isRequired,
        chapter_cover_page_color: PropTypes.string.isRequired,
        vendor_email_color: PropTypes.string.isRequired,
        vendor_section_color: PropTypes.string.isRequired,
    }).isRequired,
};

type Props = InferProps<typeof DocumentSettingsTab.propTypes>;

export default DocumentSettingsTab; 